import { useApiQuery } from "@gladis/api-client";
import {
  AmountHistory,
  ChartControls,
  DeleteEntryDialog,
  EndSourceDialog,
  EntryForm,
  EntryTable,
  IncomeChart,
  IncomeStats,
  RemoveSourceDialog,
  SourceForm,
  SourceTable,
  useArchiveIncomeSource,
  useCreateIncomeSource,
  useDeleteIncomeEntry,
  useEditIncomeEntry,
  useEditIncomeSource,
  useEndIncomeSource,
  useIncomePage,
  useRecordIncomeAmount,
  useRecordIncomeEntry,
  useRemoveIncomeAmount,
  type AmountPeriodDto,
  type IncomeEntryDto,
  type IncomeGranularity,
  type IncomeSourceDto,
} from "@gladis/domain-income/ui";
import type { CurrentUserDto, MemberDto } from "@gladis/shared";
import { Button, ErrorState, LoadingState } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/income")({
  component: Income,
});

/**
 * Income.
 *
 * Composition only — the chart, the cards, the lists and the forms come from the income package,
 * which owns what an income is. This route decides where they appear and holds the dialog state.
 *
 * ==========================================================================================
 * THIS PAGE COMPOSES NOTHING ELSE
 * ==========================================================================================
 *
 * It reads income and no other financial context. Income measured against outgoings is a question
 * about two contexts at once, and in GLADIS that question is answered on the Dashboard — which is
 * the only surface permitted to compose pillars, and where 013's household-health card will live.
 *
 * Adding an expenses import here would be the quiet start of a second answer to "what's left",
 * computed differently from the dashboard's.
 *
 * THE MEMBER LIST IS FETCHED FOR THE FORMS ONLY. The server resolves attribution from identity's own
 * answer and validates against it; this copy exists so a picker can OFFER the right names. Offering a
 * wrong one would be refused rather than accepted, which is the difference between a convenience and
 * a source of truth.
 */
function Income(): ReactNode {
  const t = useT();
  const [granularity, setGranularity] = useState<IncomeGranularity>("year");
  const [months, setMonths] = useState(12);

  const [addingSource, setAddingSource] = useState(false);
  /*
   * The ROW rather than an id, in every one of these.
   *
   * A form fills its fields from the row it was opened with; holding an id would mean looking the row
   * up again from a payload that may have refetched in between, which is how a form comes to show a
   * stale amount.
   */
  const [editingSource, setEditingSource] = useState<IncomeSourceDto | null>(null);
  const [historyFor, setHistoryFor] = useState<IncomeSourceDto | null>(null);
  const [addingEntry, setAddingEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState<IncomeEntryDto | null>(null);
  /*
   * The lifecycle dialogs, each holding the ROW it was opened for.
   *
   * Ending and removing are separate state, not one dialog with a mode: they ask different questions
   * and the removal dialog can hand over to the ending one, which needs both to exist at once for
   * the moment of the swap.
   */
  const [endingSource, setEndingSource] = useState<IncomeSourceDto | null>(null);
  const [removingSource, setRemovingSource] = useState<IncomeSourceDto | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<IncomeEntryDto | null>(null);

  const page = useIncomePage(granularity, months);

  // Served from the cache `_authenticated` already populated — no extra request.
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const householdId = me.data?.activeHousehold?.id;
  const members = useApiQuery<{ members: MemberDto[] }>(
    ["members", householdId],
    `/households/${householdId}/members`,
    { enabled: Boolean(householdId) },
  );

  const createSource = useCreateIncomeSource();
  const editSource = useEditIncomeSource();
  const recordAmount = useRecordIncomeAmount();
  const removeAmount = useRemoveIncomeAmount();
  const endSource = useEndIncomeSource();
  const archiveSource = useArchiveIncomeSource();
  const recordEntry = useRecordIncomeEntry();
  const editEntry = useEditIncomeEntry();
  const deleteEntry = useDeleteIncomeEntry();

  if (page.isPending) return <LoadingState>{t("page.income.loading")}</LoadingState>;
  if (page.isError || !page.data) return <ErrorState>{t("page.income.loadFailed")}</ErrorState>;

  const data = page.data;
  const memberOptions = (members.data?.members ?? []).map((member) => ({
    userId: member.userId,
    displayName: member.displayName,
  }));

  /*
   * The history panel's rows come from the payload the page already has.
   *
   * The read model fetches every period for the household anyway — the chart needs them — so they
   * ride along per source rather than costing a request each time the panel opens. Read from the
   * LIVE payload rather than from `historyFor`, which is the row as it was when the panel opened and
   * would show a stale history after a raise was recorded into it.
   */
  const historyPeriods: AmountPeriodDto[] = historyFor
    ? ((data.sources.find((source) => source.id === historyFor.id)?.periods ??
        []) as AmountPeriodDto[])
    : [];

  return (
    <Page>
      <Page.Title
        actions={
          <div className="flex gap-2">
            <Button onClick={() => setAddingEntry(true)}>{t("page.income.recordOneOff")}</Button>
            <Button variant="primary" onClick={() => setAddingSource(true)}>
              {t("inc.source.add")}
            </Button>
          </div>
        }
      >
        {t("nav.income")}
      </Page.Title>

      <Page.Body>
        <div className="col-span-full flex flex-col gap-10">
          <IncomeStats page={data} members={members.data?.members ?? []} />

          {/*
           * Each section is a CARD — `rounded-control border border-line bg-panel p-5`, the idiom
           * `ExpenseList` and `MembersList` already use. The chart had been sitting on the page
           * background with nothing holding it, which made it read as decoration between two
           * sections rather than as one of the three things the page is.
           */}
          <section className="rounded-control border border-line bg-panel p-5">
            <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold">{t("page.income.heading")}</h2>
                {data.chart.buckets.some((bucket) => bucket.projectedTotalInEuros !== null) ? (
                  <p className="mt-1 text-sm text-muted">{t("page.income.projectionHint")}</p>
                ) : null}
              </div>
              <ChartControls
                granularity={granularity}
                months={months}
                onGranularity={setGranularity}
                onMonths={setMonths}
              />
            </header>
            <IncomeChart page={data} />
          </section>

          <section className="rounded-control border border-line bg-panel p-5">
            <h2 className="mb-4 text-xl font-semibold">{t("inc.source.heading")}</h2>
            <SourceTable
              sources={data.sources}
              members={members.data?.members ?? []}
              onEdit={setEditingSource}
              onHistory={setHistoryFor}
              onEnd={setEndingSource}
              onResume={(source) => endSource.mutate({ sourceId: source.id, endedOn: null })}
              onArchive={setRemovingSource}
            />
          </section>

          <section className="rounded-control border border-line bg-panel p-5">
            <h2 className="mb-4 text-xl font-semibold">{t("inc.entry.heading")}</h2>
            <EntryTable
              entries={data.entries}
              members={members.data?.members ?? []}
              // Resolved here rather than sent per entry: the page already has every source, and
              // repeating a name on each row would let the two disagree after a rename.
              sourceNames={new Map(data.sources.map((source) => [source.id, source.name]))}
              onEdit={setEditingEntry}
              onDelete={setDeletingEntry}
            />
          </section>
        </div>
      </Page.Body>

      {addingSource || editingSource ? (
        <SourceForm
          open
          onClose={() => {
            setAddingSource(false);
            setEditingSource(null);
          }}
          editing={editingSource}
          members={memberOptions}
          pending={createSource.isPending || editSource.isPending}
          error={createSource.error?.message ?? editSource.error?.message ?? null}
          onCreate={(input) =>
            createSource.mutate(input, { onSuccess: () => setAddingSource(false) })
          }
          onSave={(input) => {
            if (!editingSource) return;
            editSource.mutate(
              { id: editingSource.id, ...input },
              { onSuccess: () => setEditingSource(null) },
            );
          }}
        />
      ) : null}

      {historyFor ? (
        <AmountHistory
          open
          onClose={() => setHistoryFor(null)}
          source={historyFor}
          periods={historyPeriods}
          pending={recordAmount.isPending || removeAmount.isPending}
          error={recordAmount.error?.message ?? removeAmount.error?.message ?? null}
          onRecord={(input) => recordAmount.mutate({ sourceId: historyFor.id, ...input })}
          onRemove={(periodId) => removeAmount.mutate({ sourceId: historyFor.id, periodId })}
        />
      ) : null}

      {endingSource ? (
        <EndSourceDialog
          open
          onClose={() => setEndingSource(null)}
          source={endingSource}
          // The server's date, not the browser's — the two can disagree across midnight, and the
          // whole page is computed against the server's.
          today={data.asOf}
          pending={endSource.isPending}
          error={endSource.error?.message ?? null}
          onConfirm={(endedOn) =>
            endSource.mutate(
              { sourceId: endingSource.id, endedOn },
              { onSuccess: () => setEndingSource(null) },
            )
          }
        />
      ) : null}

      {removingSource ? (
        <RemoveSourceDialog
          open
          onClose={() => setRemovingSource(null)}
          source={removingSource}
          pending={archiveSource.isPending}
          error={archiveSource.error?.message ?? null}
          onConfirm={() =>
            archiveSource.mutate(
              { sourceId: removingSource.id, archived: true },
              { onSuccess: () => setRemovingSource(null) },
            )
          }
          // The hand-over: most people reaching for "Remove" mean "it stopped", and this takes them
          // there rather than telling them to close it and find the other control.
          onEndInstead={() => {
            setEndingSource(removingSource);
            setRemovingSource(null);
          }}
        />
      ) : null}

      {deletingEntry ? (
        <DeleteEntryDialog
          open
          onClose={() => setDeletingEntry(null)}
          entry={deletingEntry}
          pending={deleteEntry.isPending}
          error={deleteEntry.error?.message ?? null}
          onConfirm={() =>
            deleteEntry.mutate(
              { id: deletingEntry.id },
              { onSuccess: () => setDeletingEntry(null) },
            )
          }
        />
      ) : null}

      {addingEntry || editingEntry ? (
        <EntryForm
          open
          onClose={() => {
            setAddingEntry(false);
            setEditingEntry(null);
          }}
          editing={editingEntry}
          sources={data.sources}
          pending={recordEntry.isPending || editEntry.isPending}
          error={recordEntry.error?.message ?? editEntry.error?.message ?? null}
          onSubmit={(input) => {
            if (editingEntry) {
              const { incomeSourceId: _ignored, ...rest } = input;
              editEntry.mutate(
                { id: editingEntry.id, ...rest },
                { onSuccess: () => setEditingEntry(null) },
              );
            } else {
              recordEntry.mutate(input, { onSuccess: () => setAddingEntry(false) });
            }
          }}
        />
      ) : null}
    </Page>
  );
}
